/**
 * DataTable - Virtualized, sortable, selectable table component.
 *
 * The workhorse of Master-Detail features. Implements the spec from
 * STREAM_SHARED_COMPONENTS.md "Detailed API Specifications".
 *
 * @example Basic usage
 * ```tsx
 * <DataTable
 *   columns={[
 *     { id: 'name', label: 'Name', accessor: 'name' },
 *     { id: 'status', label: 'Status', accessor: 'status' },
 *   ]}
 *   data={customers}
 *   onRowClick={(row) => selectCustomer(row)}
 * />
 * ```
 *
 * @example With selection and sorting
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   data={data}
 *   selection="multi"
 *   onSelectionChange={(ids) => setSelectedIds(ids)}
 *   onSortChange={(sort) => handleSort(sort)}
 * />
 * ```
 */

import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useId,
  forwardRef,
  useImperativeHandle,
  type CSSProperties,
  type ReactNode,
  type KeyboardEvent,
  type Ref,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, ChevronDown } from 'lucide-react'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { EmptyState } from '@/shared/components/EmptyState'
import { Skeleton } from '@/shared/components/ui/skeleton'

// ── Types ────────────────────────────────────────────────────────────────────

export type TableDensity = 'compact' | 'comfortable'
export type TableSelection = 'none' | 'single' | 'multi'
export type TableStriped = 'none' | 'subtle' | 'rows'
export type TableBorder = 'none' | 'horizontal' | 'all'
export type SortDirection = 'asc' | 'desc' | null

export interface ColumnDef<T> {
  id: string
  label: string
  accessor: keyof T | ((row: T) => unknown)
  width?: number | string
  minWidth?: number
  maxWidth?: number
  sortable?: boolean
  align?: 'left' | 'center' | 'right'
}

export interface SortState {
  column: string | null
  direction: SortDirection
}

export interface DataTableHandle {
  /** Focus a row by index or ID */
  focusRow: (indexOrId: number | string) => void
  /** Get the currently focused row index */
  getFocusedIndex: () => number
  /** Get the currently focused row ID */
  getFocusedRowId: () => string | null
  /** Clear focus */
  clearFocus: () => void
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: ColumnDef<T>[]
  /** Data rows */
  data: T[]
  /** Unique key accessor for each row */
  getRowId?: (row: T, index: number) => string
  /** Loading state */
  loading?: boolean
  /** Accessible label describing what this table contains (for screen readers) */
  'aria-label'?: string
  /** ID of element that labels the table (alternative to aria-label) */
  'aria-labelledby'?: string

  // ── Variants ───────────────────────────────────────────────────────────────

  /** Density mode. Default 'compact' */
  density?: TableDensity
  /** Selection mode. Default 'none' */
  selection?: TableSelection
  /** Row striping. Default 'subtle' */
  striped?: TableStriped
  /** Border style. Default 'horizontal' */
  border?: TableBorder

  // ── Slots ──────────────────────────────────────────────────────────────────

  /** Custom cell renderer */
  renderCell?: (column: ColumnDef<T>, row: T) => ReactNode
  /** Custom row wrapper (receives default row as second arg) */
  renderRow?: (row: T, defaultRow: ReactNode) => ReactNode
  /** Custom header cell renderer */
  renderHeader?: (column: ColumnDef<T>, defaultHeader: ReactNode) => ReactNode
  /** Inline expansion content */
  renderExpansion?: (row: T) => ReactNode
  /** Hover-revealed row actions */
  renderRowActions?: (row: T) => ReactNode
  /** First-use empty state */
  emptyState?: ReactNode
  /** Filtered-empty state */
  filteredEmptyState?: ReactNode
  /** Loading state override */
  loadingState?: ReactNode

  // ── Events ─────────────────────────────────────────────────────────────────

  /** Called when a row is clicked */
  onRowClick?: (row: T) => void
  /** Called when a row is double-clicked */
  onRowDoubleClick?: (row: T) => void
  /** Called when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void
  /** Called when sort changes */
  onSortChange?: (sort: SortState) => void
  /** Called when expansion changes */
  onExpansionChange?: (expandedIds: string[]) => void

  // ── Controlled State ───────────────────────────────────────────────────────

  /** Controlled selected IDs */
  selectedIds?: string[]
  /** Controlled expanded IDs */
  expandedIds?: string[]
  /** Controlled sort state */
  sortState?: SortState
  /** Controlled focused row ID */
  focusedRowId?: string
  /** Called when focused row changes */
  onFocusedRowChange?: (rowId: string | null, index: number) => void

  /**
   * The ID of the row currently shown in an inspector/sidebar.
   * When set, this row receives a visually distinct "active" treatment
   * (accent-tinted background + left border bar) that is separate from
   * the checkbox-based multi-select highlight.
   *
   * Use this in master-detail layouts where multi-select is available
   * for bulk operations but a single row is "active" in the detail pane.
   */
  activeRowId?: string | null
}

// ── Default getRowId ─────────────────────────────────────────────────────────

function defaultGetRowId<T>(row: T, index: number): string {
  const r = row as Record<string, unknown>
  return (r.id as string) ?? (r._id as string) ?? String(index)
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * DataTable renders a virtualized, accessible data table.
 *
 * Features:
 * - Virtualization via @tanstack/react-virtual (only visible rows + overscan in DOM)
 * - Keyboard: j/k, arrows, Enter, Space (toggle), Cmd+A (select all)
 * - ARIA: role="grid" with aria-rowcount, aria-colindex, aria-selected, aria-activedescendant
 * - Single/multi selection with controlled/uncontrolled modes
 * - Sort model: asc -> desc -> none on header click
 * - Inline expansion with FLIP animation for neighbors
 * - Customizable via slots
 * - Row focus API via ref for external keyboard navigation
 */
function DataTableInner<T>(
  {
    columns,
    data,
    getRowId = defaultGetRowId,
    loading = false,
    density = 'compact',
    selection = 'none',
    striped = 'subtle',
    border = 'horizontal',
    renderCell,
    renderRow,
    renderHeader,
    renderExpansion,
    renderRowActions,
    emptyState,
    filteredEmptyState,
    loadingState,
    onRowClick,
    onRowDoubleClick,
    onSelectionChange,
    onSortChange,
    onExpansionChange,
    selectedIds: controlledSelectedIds,
    expandedIds: controlledExpandedIds,
    sortState: controlledSortState,
    focusedRowId: controlledFocusedRowId,
    onFocusedRowChange,
    activeRowId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  }: DataTableProps<T>,
  ref: Ref<DataTableHandle>
): ReactNode {
  // Refs
  const parentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Stable per-table id used to namespace row DOM ids (for aria-activedescendant)
  const reactId = useId()
  const tableId = `dt-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`
  const [internalFocusedIndex, setInternalFocusedIndex] = useState<number>(-1)
  // Mirror of the focused index in a ref, updated synchronously inside the
  // imperative handle so callers observe writes immediately, regardless of
  // React's state-update batching.
  const focusedIndexRef = useRef<number>(-1)

  // Internal state (uncontrolled mode)
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([])
  const [internalExpandedIds, setInternalExpandedIds] = useState<string[]>([])
  const [internalSortState, setInternalSortState] = useState<SortState>({
    column: null,
    direction: null,
  })

  // Use controlled or internal state
  const selectedIds = controlledSelectedIds ?? internalSelectedIds
  const expandedIds = controlledExpandedIds ?? internalExpandedIds
  const sortState = controlledSortState ?? internalSortState

  // Compute focused index from controlled focusedRowId or use internal state.
  // Used for rendering (focus ring, aria-activedescendant) — represents the
  // last *committed* index after React renders.
  const focusedIndex = controlledFocusedRowId !== undefined
    ? data.findIndex((row, idx) => getRowId(row, idx) === controlledFocusedRowId)
    : internalFocusedIndex

  // Keep the ref mirror in sync with the committed render value so that ref
  // reads following a controlled-mode prop change (or an internal state
  // commit) return the latest value.
  useEffect(() => {
    focusedIndexRef.current = focusedIndex
  }, [focusedIndex])

  // Update focus helper that works with both controlled and uncontrolled modes
  const updateFocusedIndex = useCallback((newIndex: number) => {
    const clampedIndex = Math.max(-1, Math.min(newIndex, data.length - 1))

    // Synchronously update the ref so subsequent imperative reads observe the
    // new value immediately, even if React batches the upcoming render.
    focusedIndexRef.current = clampedIndex

    const newRowId = clampedIndex >= 0 ? getRowId(data[clampedIndex], clampedIndex) : null

    if (controlledFocusedRowId !== undefined) {
      // Controlled mode: emit change; consumer drives the new focusedRowId.
      onFocusedRowChange?.(newRowId, clampedIndex)
    } else {
      // Uncontrolled mode: update internal state and emit change.
      setInternalFocusedIndex(clampedIndex)
      onFocusedRowChange?.(newRowId, clampedIndex)
    }
  }, [data, getRowId, controlledFocusedRowId, onFocusedRowChange])

  // Imperative handle for external focus control. Reads use the ref so the
  // contract `focusRow(x); getFocusedIndex() === x` holds synchronously.
  useImperativeHandle(ref, () => ({
    focusRow: (indexOrId: number | string) => {
      if (typeof indexOrId === 'number') {
        updateFocusedIndex(indexOrId)
      } else {
        const idx = data.findIndex((row, i) => getRowId(row, i) === indexOrId)
        if (idx !== -1) {
          updateFocusedIndex(idx)
        }
      }
    },
    getFocusedIndex: () => focusedIndexRef.current,
    getFocusedRowId: () => {
      const idx = focusedIndexRef.current
      if (idx >= 0 && idx < data.length) {
        return getRowId(data[idx], idx)
      }
      return null
    },
    clearFocus: () => updateFocusedIndex(-1),
  }), [data, getRowId, updateFocusedIndex])

  // Row height based on density
  const rowHeight = density === 'compact' ? 36 : 44
  const headerHeight = density === 'compact' ? 32 : 40

  // Virtualization.
  //
  // estimateSize is a hint used until the row is actually rendered and
  // measured. Without measureElement, expanded rows whose natural height
  // exceeds the estimate (e.g. error-log expansions with full message + JSON
  // inspector) would overflow on top of subsequent rows because the
  // virtualizer kept positioning siblings using the stale estimate. We use
  // measureElement to read actual DOM heights and re-position accordingly;
  // each rendered row attaches `ref={virtualizer.measureElement}` and a
  // `data-index` attribute so the virtualizer can correlate measurements.
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(
      (index: number) => {
        const rowId = getRowId(data[index], index)
        return expandedIds.includes(rowId) ? rowHeight * 3 : rowHeight
      },
      [data, getRowId, expandedIds, rowHeight]
    ),
    measureElement: (el) =>
      el ? Math.ceil(el.getBoundingClientRect().height) : 0,
    overscan: 5,
  })

  // Selection handlers
  const handleSelect = useCallback(
    (rowId: string, _isShiftClick = false) => {
      if (selection === 'none') return

      let newIds: string[]
      if (selection === 'single') {
        newIds = selectedIds.includes(rowId) ? [] : [rowId]
      } else {
        // Multi select
        if (selectedIds.includes(rowId)) {
          newIds = selectedIds.filter((id) => id !== rowId)
        } else {
          newIds = [...selectedIds, rowId]
        }
      }

      if (controlledSelectedIds === undefined) {
        setInternalSelectedIds(newIds)
      }
      onSelectionChange?.(newIds)
    },
    [selection, selectedIds, controlledSelectedIds, onSelectionChange]
  )

  const handleSelectAll = useCallback(() => {
    if (selection !== 'multi') return

    const allIds = data.map((row, i) => getRowId(row, i))
    const newIds = selectedIds.length === data.length ? [] : allIds

    if (controlledSelectedIds === undefined) {
      setInternalSelectedIds(newIds)
    }
    onSelectionChange?.(newIds)
  }, [selection, data, getRowId, selectedIds, controlledSelectedIds, onSelectionChange])

  // Expansion handlers
  const handleExpand = useCallback(
    (rowId: string) => {
      if (!renderExpansion) return

      const newIds = expandedIds.includes(rowId)
        ? expandedIds.filter((id) => id !== rowId)
        : [...expandedIds, rowId]

      if (controlledExpandedIds === undefined) {
        setInternalExpandedIds(newIds)
      }
      onExpansionChange?.(newIds)
    },
    [renderExpansion, expandedIds, controlledExpandedIds, onExpansionChange]
  )

  // Sort handler
  const handleSort = useCallback(
    (columnId: string) => {
      const column = columns.find((c) => c.id === columnId)
      if (!column?.sortable) return

      let newDirection: SortDirection
      if (sortState.column !== columnId) {
        newDirection = 'asc'
      } else if (sortState.direction === 'asc') {
        newDirection = 'desc'
      } else {
        newDirection = null
      }

      const newState: SortState = {
        column: newDirection ? columnId : null,
        direction: newDirection,
      }

      if (controlledSortState === undefined) {
        setInternalSortState(newState)
      }
      onSortChange?.(newState)
    },
    [columns, sortState, controlledSortState, onSortChange]
  )

  // Reset focused index when data changes and current index is out of bounds
  useEffect(() => {
    if (focusedIndex >= data.length) {
      updateFocusedIndex(data.length > 0 ? data.length - 1 : -1)
    }
  }, [data.length, focusedIndex, updateFocusedIndex])

  // Invalidate cached row measurements whenever the expanded set changes.
  //
  // Background: when a row toggles from expanded to collapsed, React swaps
  // out the expanded wrapper (a tall element, e.g. 400px) for the bare
  // defaultRow (~36px). The virtualizer uses ResizeObserver to track row
  // heights, but ResizeObserver only fires for elements that remain mounted
  // through a size change — when an element unmounts and a different element
  // mounts in its place at the same `data-index`, the cached measurement for
  // that index is left over from the previous element. Result: the
  // virtualizer's total scroll size and the translateY positions of
  // downstream rows are computed from the stale (expanded) measurement, the
  // table renders a "ghost" tall slot where the expansion used to be, and
  // visible rows appear pushed off the bottom of the viewport.
  //
  // Calling `measure()` resets every cached measurement; the virtualizer
  // then re-measures each rendered row on the next layout via the existing
  // `measureElement` ref bindings. This is cheap (a single layout read per
  // visible row) and only fires on user-initiated expand/collapse, so the
  // cost is negligible compared to the alternative of leaving rows in the
  // wrong position.
  const lastExpandedIdsRef = useRef<string[]>(expandedIds)
  useEffect(() => {
    if (lastExpandedIdsRef.current !== expandedIds) {
      lastExpandedIdsRef.current = expandedIds
      virtualizer.measure()
    }
  }, [expandedIds, virtualizer])

  // Keep the focused row in the visible scroll window. External keyboard
  // owners (page-level j/k handlers calling `focusRow()`) should not have to
  // duplicate scroll-to-view logic; the table owns this so the focus ring
  // stays on screen.
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < data.length) {
      virtualizer.scrollToIndex(focusedIndex, { align: 'auto' })
    }
  }, [focusedIndex, data.length, virtualizer])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (data.length === 0) return

      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault()
          // Initialize to first row if no focus yet
          if (focusedIndex < 0) {
            updateFocusedIndex(0)
          } else {
            updateFocusedIndex(focusedIndex + 1)
          }
          break
        case 'k':
        case 'ArrowUp':
          e.preventDefault()
          // Initialize to last row if no focus yet
          if (focusedIndex < 0) {
            updateFocusedIndex(data.length - 1)
          } else {
            updateFocusedIndex(focusedIndex - 1)
          }
          break
        case 'Enter':
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < data.length) {
            const row = data[focusedIndex]
            onRowClick?.(row)
          }
          break
        case ' ':
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < data.length && selection !== 'none') {
            const rowId = getRowId(data[focusedIndex], focusedIndex)
            handleSelect(rowId)
          }
          break
        case 'a':
          if ((e.metaKey || e.ctrlKey) && selection === 'multi') {
            e.preventDefault()
            handleSelectAll()
          }
          break
        case 'Escape':
          if (selection !== 'none' && selectedIds.length > 0) {
            e.preventDefault()
            if (controlledSelectedIds === undefined) {
              setInternalSelectedIds([])
            }
            onSelectionChange?.([])
          }
          break
      }
    },
    [
      data,
      focusedIndex,
      selection,
      selectedIds,
      getRowId,
      handleSelect,
      handleSelectAll,
      onRowClick,
      onSelectionChange,
      controlledSelectedIds,
      updateFocusedIndex,
    ]
  )

  // Get cell value
  const getCellValue = useCallback((column: ColumnDef<T>, row: T): unknown => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row)
    }
    return row[column.accessor]
  }, [])

  // Render loading state
  if (loading) {
    if (loadingState) return loadingState
    return (
      <div
        style={styles.skeleton}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={ariaLabel ? `Loading ${ariaLabel}` : 'Loading data'}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} style={{ height: rowHeight, marginBottom: 2 }} />
        ))}
      </div>
    )
  }

  // Render empty state
  if (data.length === 0) {
    return (
      filteredEmptyState ??
      emptyState ?? (
        <EmptyState
          variant="first-use"
          title="No data"
          description="Data will appear here once available."
        />
      )
    )
  }

  const virtualRows = virtualizer.getVirtualItems()

  // Compute aria-activedescendant — must be the DOM `id` of a descendant row.
  // We render rows with `id={`${tableId}-row-${rowId}`}` below; the value here
  // must match exactly so screen readers can resolve the descendant.
  const activedescendant = focusedIndex >= 0 && focusedIndex < data.length
    ? `${tableId}-row-${getRowId(data[focusedIndex], focusedIndex)}`
    : undefined

  return (
    <div
      ref={containerRef}
      style={styles.container}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="grid"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-rowcount={data.length}
      aria-colcount={columns.length + (selection !== 'none' ? 1 : 0)}
      aria-activedescendant={activedescendant}
      aria-multiselectable={selection === 'multi' ? true : undefined}
    >
      {/* Screen reader announcements for selection changes */}
      {selection !== 'none' && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={srOnlyStyles}
        >
          {selectedIds.length > 0
            ? `${selectedIds.length} ${selectedIds.length === 1 ? 'row' : 'rows'} selected`
            : ''}
        </div>
      )}
      {/* Header */}
      <div
        style={{
          ...styles.header,
          height: headerHeight,
          borderBottom: border !== 'none' ? '1px solid var(--datatable-row-border)' : undefined,
        }}
        role="row"
      >
        {/* Selection header */}
        {selection === 'multi' && (
          <div style={{ ...styles.headerCell, ...styles.selectionCell, flex: '0 0 44px' }} role="columnheader">
            <Checkbox
              checked={selectedIds.length === data.length && data.length > 0}
              onCheckedChange={() => handleSelectAll()}
              aria-label="Select all rows"
            />
          </div>
        )}

        {/* Expansion header */}
        {renderExpansion && (
          <div style={{ ...styles.headerCell, flex: '0 0 32px' }} role="columnheader" />
        )}

        {/* Column headers */}
        {columns.map((column, colIndex) => {
          const isSorted = sortState.column === column.id
          const sortIcon = !column.sortable ? null : isSorted ? (
            sortState.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
          ) : (
            <ArrowUpDown size={12} style={{ opacity: 0.4 }} />
          )

          // Build descriptive aria-label for sort buttons
          const sortAriaLabel = column.sortable
            ? isSorted
              ? sortState.direction === 'asc'
                ? `${column.label}, sorted ascending. Click to sort descending.`
                : `${column.label}, sorted descending. Click to remove sort.`
              : `${column.label}. Click to sort ascending.`
            : undefined

          const defaultHeader = (
            <div
              style={{
                ...styles.headerCell,
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                textAlign: column.align,
                cursor: column.sortable ? 'pointer' : undefined,
              }}
              onClick={() => column.sortable && handleSort(column.id)}
              onKeyDown={(e) => {
                if (column.sortable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  handleSort(column.id)
                }
              }}
              role="columnheader"
              tabIndex={column.sortable ? 0 : undefined}
              aria-colindex={colIndex + 1 + (selection !== 'none' ? 1 : 0)}
              aria-sort={isSorted ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : column.sortable ? 'none' : undefined}
              aria-label={sortAriaLabel}
            >
              <span>{column.label}</span>
              {sortIcon}
            </div>
          )

          return renderHeader ? renderHeader(column, defaultHeader) : defaultHeader
        })}

        {/* Actions header */}
        {renderRowActions && (
          <div style={{ ...styles.headerCell, flex: '0 0 80px' }} role="columnheader" />
        )}
      </div>

      {/* Virtualized body */}
      <div
        ref={parentRef}
        style={styles.body}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualRows.map((virtualRow) => {
            const rowIndex = virtualRow.index
            const row = data[rowIndex]
            const rowId = getRowId(row, rowIndex)
            const isSelected = selectedIds.includes(rowId)
            const isExpanded = expandedIds.includes(rowId)
            const isFocused = focusedIndex === rowIndex
            const isStriped = striped === 'rows' && rowIndex % 2 === 1
            const isActive = activeRowId != null && rowId === activeRowId

            // Active row (shown in inspector) gets accent-tinted background;
            // batch-selected rows get the standard selected background;
            // this ensures the "inspected" row is always visually distinct.
            const rowBackground = isActive
              ? 'var(--datatable-row-bg-active)'
              : isSelected
              ? 'var(--datatable-row-bg-selected)'
              : isStriped
              ? 'var(--white-overlay-subtle)'
              : 'var(--datatable-row-bg)'

            // The base row inherits styles.row which is `position: absolute`
            // and we add an explicit translateY so the virtualizer can stack
            // rows. When the row is expanded we instead embed defaultRow
            // inside a wrapper that owns the positioning — defaultRow then
            // needs to be `position: relative` so the expansion below it
            // takes its own space in normal flow rather than overlaying.
            const rowStyle: CSSProperties = isExpanded
              ? {
                  ...styles.row,
                  position: 'relative',
                  transform: 'none',
                  height: rowHeight,
                  background: rowBackground,
                  borderLeft: isActive ? '3px solid var(--datatable-row-border-active)' : undefined,
                  outline: isFocused ? 'var(--datatable-row-outline-focus)' : undefined,
                  outlineOffset: '-2px',
                }
              : {
                  ...styles.row,
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                  background: rowBackground,
                  borderLeft: isActive ? '3px solid var(--datatable-row-border-active)' : undefined,
                  borderBottom:
                    border === 'horizontal' || border === 'all'
                      ? '1px solid var(--datatable-row-border)'
                      : undefined,
                  outline: isFocused ? 'var(--datatable-row-outline-focus)' : undefined,
                  outlineOffset: '-2px',
                }

            const defaultRow = (
              <div
                key={rowId}
                id={`${tableId}-row-${rowId}`}
                ref={isExpanded ? undefined : virtualizer.measureElement}
                style={rowStyle}
                onClick={() => {
                  if (selection === 'single') {
                    handleSelect(rowId)
                  }
                  onRowClick?.(row)
                }}
                onDoubleClick={() => onRowDoubleClick?.(row)}
                role="row"
                aria-rowindex={rowIndex + 1}
                aria-selected={selection !== 'none' ? isSelected : undefined}
                aria-expanded={renderExpansion ? isExpanded : undefined}
                data-row-id={rowId}
                data-index={rowIndex}
                className="queue-item-row queue-item-hoverable"
              >
                {/* Selection checkbox */}
                {selection === 'multi' && (
                  <div style={{ ...styles.cell, ...styles.selectionCell, flex: '0 0 44px' }} role="gridcell">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleSelect(rowId)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select row ${rowIndex + 1}`}
                    />
                  </div>
                )}

                {/* Expansion toggle */}
                {renderExpansion && (
                  <div
                    style={{ ...styles.cell, flex: '0 0 32px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleExpand(rowId)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation()
                        e.preventDefault()
                        handleExpand(rowId)
                      }
                    }}
                    role="button"
                    tabIndex={-1}
                    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>
                )}

                {/* Data cells */}
                {columns.map((column, colIndex) => {
                  const value = getCellValue(column, row)
                  const cellContent = renderCell
                    ? renderCell(column, row)
                    : String(value ?? '')

                  return (
                    <div
                      key={column.id}
                      style={{
                        ...styles.cell,
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        textAlign: column.align,
                      }}
                      role="gridcell"
                      aria-colindex={colIndex + 1 + (selection !== 'none' ? 1 : 0)}
                    >
                      {cellContent}
                    </div>
                  )
                })}

                {/* Row actions */}
                {renderRowActions && (
                  <div
                    style={{ ...styles.cell, flex: '0 0 80px', justifyContent: 'flex-end' }}
                    role="gridcell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderRowActions(row)}
                  </div>
                )}
              </div>
            )

            // Render with expansion. The wrapper owns the absolute
            // positioning and carries the measureElement ref so the
            // virtualizer reads the *actual* combined height of the row +
            // expansion content. defaultRow has been switched to
            // position:relative above, so the expansion below it sits in
            // normal flow instead of overlaying the row.
            //
            // We deliberately give the expanded wrapper a *different* React
            // key from the collapsed defaultRow (`${rowId}-expanded` vs
            // `rowId`). Without this, React sees the same key on both
            // versions and reconciles the DOM node in place — but because
            // the ref function (`virtualizer.measureElement`) is identical
            // across renders, React does NOT re-invoke the ref callback,
            // and the virtualizer never re-measures the row when it
            // collapses. The result was a "ghost" tall slot left behind
            // after collapse: the row content shrank to its normal height
            // but the cached measurement remained at the expanded height,
            // so subsequent rows kept their old translateY and a gap
            // appeared. Different keys force React to unmount the expanded
            // wrapper and mount a fresh defaultRow, which fires the ref
            // callbacks on both sides and lets the virtualizer measure
            // the new (smaller) height correctly.
            if (isExpanded && renderExpansion) {
              return (
                <div
                  key={`${rowId}-expanded`}
                  ref={virtualizer.measureElement}
                  data-index={rowIndex}
                  data-row-id={rowId}
                  data-expanded="true"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    background: rowBackground,
                  }}
                >
                  {renderRow ? renderRow(row, defaultRow) : defaultRow}
                  <div style={styles.expansion}>{renderExpansion(row)}</div>
                </div>
              )
            }

            return renderRow ? renderRow(row, defaultRow) : defaultRow
          })}
        </div>
      </div>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    outline: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--datatable-header-bg)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    padding: `0 var(--datatable-cell-padding-x)`,
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--datatable-header-text)',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transition: 'background var(--transition-fast)',
    cursor: 'pointer',
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    padding: `var(--datatable-cell-padding-y) var(--datatable-cell-padding-x)`,
    fontSize: '13px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  selectionCell: {
    justifyContent: 'center',
    padding: '0 8px',
  },
  expansion: {
    padding: '12px var(--datatable-cell-padding-x)',
    paddingLeft: '48px',
    background: 'var(--bg-tertiary)',
    borderBottom: '1px solid var(--datatable-row-border)',
  },
  skeleton: {
    padding: '16px',
  },
}

// Screen reader only styles
const srOnlyStyles: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
}

// Export with forwardRef, preserving generic type parameter
export const DataTable = forwardRef(DataTableInner) as <T>(
  props: DataTableProps<T> & { ref?: Ref<DataTableHandle> }
) => ReactNode

export default DataTable
